import React, { useState, useEffect, useCallback } from 'react';
import {
  getAgencyClients,
  createAgencyClient,
  updateAgencyClient,
  deleteAgencyClient,
  toggleAgencyClientService,
  updateAgencyClientTier,
} from '../../services/firestoreService';

const TIERS = {
  starter: { name: 'Starter', monthly: 79, color: '#4ADE80', services: ['website'] },
  professional: { name: 'Professional', monthly: 199, color: '#60A5FA', services: ['website', 'chatbot', 'analytics', 'reviews'] },
  premium: { name: 'Premium', monthly: 399, color: '#A78BFA', services: ['website', 'chatbot', 'analytics', 'reviews', 'phone', 'booking', 'seo', 'lead-nurture'] },
  enterprise: { name: 'Enterprise', monthly: 0, color: '#FBBF24', services: ['website', 'chatbot', 'analytics', 'reviews', 'phone', 'booking', 'seo', 'lead-nurture', 'custom'] },
};

const SERVICE_LABELS = {
  website: 'Website',
  chatbot: 'AI Chatbot',
  phone: 'Phone Agent',
  reviews: 'Reviews',
  booking: 'Booking',
  analytics: 'Analytics',
  seo: 'Full SEO',
  'lead-nurture': 'Lead Nurture',
  custom: 'Custom AI',
};

const SEED_CLIENTS = [
  {
    name: 'Minutemen Land Services',
    owner: 'Jordan Grant',
    phone: '(203) 247-7744',
    address: '377 Highland Ave, Norwalk, CT',
    tier: 'starter',
    status: 'contract_sent',
    siteUrl: 'https://apn-demos.vercel.app/minutemen-land-services',
    domain: 'minutemenlandscaping.com',
    domainStatus: 'available',
    setupPaid: false,
    monthlyActive: false,
    services: { website: true, chatbot: false, phone: false, reviews: false, booking: false, analytics: false, seo: false, 'lead-nurture': false },
    notes: 'Wants AI upgrade after site delivers. Founded 1978.',
    hubspotId: '315540040406',
    retellAgentId: '',
    chatbotId: '',
  },
  {
    name: 'A to Z Property Maintenance',
    owner: 'Daimeon Baity',
    phone: '(203) 775-9174',
    address: 'Brookfield, CT',
    tier: 'starter',
    status: 'interested',
    siteUrl: 'https://apn-demos.vercel.app/a-to-z-property-maintenance-llc',
    domain: '',
    domainStatus: '',
    setupPaid: false,
    monthlyActive: false,
    services: { website: true, chatbot: false, phone: false, reviews: false, booking: false, analytics: false, seo: false, 'lead-nurture': false },
    notes: 'Showed interest. Email outreach pending.',
    hubspotId: '315578519252',
    retellAgentId: '',
    chatbotId: '',
  },
];

const STATUS_CONFIG = {
  contract_sent: { label: 'Contract Sent', bg: '#FBBF2420', color: '#FBBF24' },
  interested: { label: 'Interested', bg: '#60A5FA20', color: '#60A5FA' },
  active: { label: 'Active', bg: '#4ADE8020', color: '#4ADE80' },
  onboarding: { label: 'Onboarding', bg: '#A78BFA20', color: '#A78BFA' },
  paused: { label: 'Paused', bg: '#F8717120', color: '#F87171' },
};

export default function AgencyClients() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [view, setView] = useState('overview');
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      let data = await getAgencyClients();
      if (data.length === 0) {
        // Seed initial clients on first load
        for (const seed of SEED_CLIENTS) {
          await createAgencyClient(seed);
        }
        data = await getAgencyClients();
      }
      setClients(data);
    } catch (err) {
      console.error('Failed to load agency clients:', err);
      // Fallback to seed data if Firestore fails
      setClients(SEED_CLIENTS.map((c, i) => ({ ...c, id: `seed-${i}` })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const totalMRR = clients.filter(c => c.monthlyActive).reduce((sum, c) => sum + (TIERS[c.tier]?.monthly || 0), 0);
  const activeCount = clients.filter(c => c.status === 'active').length;
  const pipelineCount = clients.filter(c => ['contract_sent', 'interested', 'onboarding'].includes(c.status)).length;

  const toggleService = async (clientId, service) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    const tierServices = TIERS[client.tier]?.services || [];
    if (!tierServices.includes(service)) {
      alert(`${SERVICE_LABELS[service]} requires upgrading to a higher tier.`);
      return;
    }
    const newVal = !client.services[service];
    try {
      await toggleAgencyClientService(clientId, service, newVal);
      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, services: { ...c.services, [service]: newVal } } : c
      ));
    } catch (err) {
      console.error('Failed to toggle service:', err);
    }
  };

  const changeTier = async (clientId, newTier) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    const tierServices = TIERS[newTier]?.services || [];
    const updatedServices = {};
    Object.keys(SERVICE_LABELS).forEach(s => {
      updatedServices[s] = tierServices.includes(s) ? (client.services[s] || false) : false;
    });
    try {
      await updateAgencyClientTier(clientId, newTier);
      await updateAgencyClient(clientId, { services: updatedServices });
      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, tier: newTier, services: updatedServices } : c
      ));
    } catch (err) {
      console.error('Failed to change tier:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '64px', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: '18px', fontWeight: '500' }}>Loading agency clients...</div>
      </div>
    );
  }

  if (selectedClient) {
    const client = clients.find(c => c.id === selectedClient);
    if (!client) { setSelectedClient(null); return null; }
    const tierInfo = TIERS[client.tier];
    const tierServices = tierInfo?.services || [];

    return (
      <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
        <button onClick={() => setSelectedClient(null)}
          style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', marginBottom: '24px', fontSize: '14px', color: '#374151' }}>
          ← Back to All Clients
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>{client.name}</h1>
            <p style={{ color: '#6b7280', fontSize: '15px' }}>{client.owner} · {client.phone} · {client.address}</p>
          </div>
          <span style={{ padding: '6px 16px', borderRadius: '100px', fontSize: '13px', fontWeight: '600', background: STATUS_CONFIG[client.status]?.bg, color: STATUS_CONFIG[client.status]?.color }}>
            {STATUS_CONFIG[client.status]?.label}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '32px' }}>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Tier</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{tierInfo?.name}</div>
          </div>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Monthly</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>${tierInfo?.monthly}/mo</div>
          </div>
          <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Setup</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{client.setupPaid ? 'Paid' : 'Pending'}</div>
          </div>
          <div style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#ca8a04', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Domain</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{client.domain || 'Not set'}</div>
          </div>
        </div>

        {/* Site Preview */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>Website</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <a href={client.siteUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: '#2563eb', fontSize: '14px' }}>{client.siteUrl}</a>
            <button style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
              Deploy to Vercel
            </button>
            {client.domain && (
              <button style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                Connect Domain
              </button>
            )}
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', height: '400px' }}>
            <iframe src={client.siteUrl} title="Site Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
          </div>
        </div>

        {/* Service Toggles */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>AI Services</h2>
            <select value={client.tier} onChange={e => changeTier(client.id, e.target.value)}
              style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', fontWeight: '500' }}>
              <option value="starter">Starter — $79/mo</option>
              <option value="professional">Professional — $199/mo</option>
              <option value="premium">Premium — $399/mo</option>
              <option value="enterprise">Enterprise — Custom</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {Object.entries(SERVICE_LABELS).map(([key, label]) => {
              const isIncluded = tierServices.includes(key);
              const isActive = client.services[key];
              return (
                <div key={key} style={{
                  padding: '16px', borderRadius: '10px',
                  border: `1px solid ${isActive ? '#4ADE8040' : '#e5e7eb'}`,
                  background: isActive ? '#f0fdf4' : (isIncluded ? '#fff' : '#f9fafb'),
                  opacity: isIncluded ? 1 : 0.5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{label}</div>
                    <div style={{ fontSize: '12px', color: isIncluded ? '#16a34a' : '#9ca3af' }}>
                      {isIncluded ? (isActive ? 'Active' : 'Available') : 'Upgrade required'}
                    </div>
                  </div>
                  {isIncluded && (
                    <button onClick={() => toggleService(client.id, key)}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                        background: isActive ? '#4ADE80' : '#d1d5db', position: 'relative', transition: 'background .2s',
                      }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '10px', background: '#fff',
                        position: 'absolute', top: '2px', transition: 'left .2s',
                        left: isActive ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                      }} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>Notes</h2>
          <p style={{ color: '#4b5563', fontSize: '14px', lineHeight: '1.7' }}>{client.notes}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>Agency Clients</h1>
          <p style={{ color: '#6b7280', fontSize: '15px', marginTop: '4px' }}>Manage web design clients and AI services</p>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', borderTop: '3px solid #4ADE80' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Active Clients</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{activeCount}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', borderTop: '3px solid #60A5FA' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>In Pipeline</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{pipelineCount}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', borderTop: '3px solid #FBBF24' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Monthly Revenue</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>${totalMRR}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', borderTop: '3px solid #A78BFA' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>Total Clients</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{clients.length}</div>
        </div>
      </div>

      {/* Client Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {clients.map(client => {
          const tierInfo = TIERS[client.tier];
          return (
            <div key={client.id} onClick={() => setSelectedClient(client.id)}
              style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', cursor: 'pointer', transition: 'all .2s', borderLeft: `4px solid ${tierInfo?.color}` }}
              onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,.08)'}
              onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{client.name}</div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>{client.owner} · {client.phone}</div>
                </div>
                <span style={{ padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: '600', background: STATUS_CONFIG[client.status]?.bg, color: STATUS_CONFIG[client.status]?.color }}>
                  {STATUS_CONFIG[client.status]?.label}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', background: `${tierInfo?.color}20`, color: tierInfo?.color }}>
                  {tierInfo?.name}
                </span>
                <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '12px', background: '#f3f4f6', color: '#6b7280' }}>
                  ${tierInfo?.monthly}/mo
                </span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {Object.entries(client.services).map(([key, active]) => (
                  <span key={key} style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                    background: active ? '#4ADE8015' : '#f3f4f6',
                    color: active ? '#16a34a' : '#9ca3af',
                    border: `1px solid ${active ? '#4ADE8030' : '#e5e7eb'}`,
                  }}>
                    {SERVICE_LABELS[key] || key}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Agent Status */}
      <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
        AI Agent Manager
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Chatbots</div>
          {clients.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
              <span style={{ color: '#374151' }}>{c.name}</span>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.services.chatbot ? '#4ADE80' : '#d1d5db' }} />
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Phone Agents</div>
          {clients.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
              <span style={{ color: '#374151' }}>{c.name}</span>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.services.phone ? '#4ADE80' : '#d1d5db' }} />
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Review Systems</div>
          {clients.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
              <span style={{ color: '#374151' }}>{c.name}</span>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.services.reviews ? '#4ADE80' : '#d1d5db' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
